import { useNavigate } from "@solidjs/router";
import {
    Match,
    Suspense,
    Switch,
    createEffect,
    createSignal,
    type ParentComponent,
    ErrorBoundary,
} from "solid-js";
import { Loader } from "src/components/Loader";
import { Error } from "./Error";
import {
    StageType,
    ErrorSeverity,
} from "../domain";
import type { RedirectInterface } from "../domain/models/RedirectInterface";
import { getStageErrorSeverity, matchStageType } from "../utilities";
import { useStageContext, StageContextProvider } from "./StageContext";

export const NonChallengeStateRendererInner: ParentComponent = (props) => {
    const navigate = useNavigate();
    const { stageContext } = useStageContext();

    createEffect(() => {
        if (matchStageType<RedirectInterface>(stageContext(), StageType.REDIRECT) &&
            // @ts-expect-error StageContext cannot be undefined after matching as redirect type.
            ("url" in stageContext().data)
        ) {
            // Due to some weird behaviour with the back button and state management on the select challenge
            //  we have to navigate to a separate page that redirects us further, instead of redirecting directly
            // @ts-expect-error StageContext cannot be undefined after matching as redirect type.
            // eslint-disable-next-line typescript/no-unsafe-argument
            sessionStorage.setItem("redirect", stageContext().data?.url);
            navigate("/redirect");
        }
    });

    const [isInError, setInError] = createSignal(false);

    return (
        <Suspense fallback={<Loader dialog delay />}>
            <ErrorBoundary fallback={(err) => {
                // Stop the keep alive when an error is shown
                if (!isInError()) {
                    setInError(true);
                }
                return <Error stageData={stageContext()} exception={err} disableChallengeRefetch={true} />;
            }}
            >
                {/* Note that all stageData uses the `latest` property
                        to prevent Suspense from kicking in and flashing the interface */}
                <Switch fallback={
                    <>{(() => {
                        // Stop the keep alive when an error is shown
                        if (!isInError()) {
                            setInError(true);
                        }
                        return <Error stageData={stageContext()} disableChallengeRefetch={true} />;
                    })()}
                    </>
                }
                >
                    <Match when={!stageContext() || getStageErrorSeverity(stageContext()) <= ErrorSeverity.ERROR}>
                        {/* eslint-disable-next-line typescript/no-confusing-void-expression */}
                        {(() => {
                            if (isInError()) {
                                setInError(false);
                            }
                            return undefined;
                        })()}
                        {props.children}
                    </Match>
                </Switch>
            </ErrorBoundary>
        </Suspense>
    );
};

export const NonChallengeStateRenderer: ParentComponent = (props) => (
    <StageContextProvider>
        <NonChallengeStateRendererInner>
            {props.children}
        </NonChallengeStateRendererInner>
    </StageContextProvider>
);
